import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { Dialog, DialogPanel } from '@headlessui/react';
import BaseButton from 'flow/components/ui-components/BaseButton';
import TransferTitleItem from './TransferTitleItem';
import TransferOptionRow from 'flow/components/call-transfer-dialog/TransferOptionRow';
import InfoBox, { InfoBoxType } from 'flow/components/ui-components/InfoBox';
import { RowTabStopContext } from 'flow/components/ui-components/row-tab-stop';
import Icon from 'flow/components/ui-components/Icon';
import { makeGetContactDataForNumber } from 'flow/selectors/contact-selectors';
import { getFocusedActiveCall } from 'flow/selectors/call-selectors';
import { extractName } from 'flow/utils';
import { lookupNumber } from 'flow/actions/contact-actions';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { getNumberOfOtherPerson, prettyFormatE164Number } from 'flow/utils/call-utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var BlindTransferRow = function BlindTransferRow(_ref) {
  var initCall = _ref.initCall;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var primaryText = i18next.t('General.Call.Transfer.Alert.Blind');
  var secondaryText = i18next.t('General.Call.Transfer.Alert.Blind.Subtitle');
  return /*#__PURE__*/_jsx(TransferOptionRow, {
    initCall: initCall,
    primaryText: primaryText,
    secondaryText: secondaryText,
    transferOptionIcon: /*#__PURE__*/_jsx(Icon, {
      iconKey: "transfer_call"
    }),
    testId: 'direct-transfer',
    componentId: 'blind'
  });
};
var AttendedTransferRow = function AttendedTransferRow(_ref2) {
  var initCall = _ref2.initCall;
  var _useAppContext2 = useAppContext(),
    i18next = _useAppContext2.i18next;
  var primaryText = i18next.t('General.Call.Transfer.Alert.Attended');
  var secondaryText = i18next.t('General.Call.Transfer.Alert.Attended.Subtitle');
  return /*#__PURE__*/_jsx(TransferOptionRow, {
    initCall: initCall,
    primaryText: primaryText,
    secondaryText: secondaryText,
    transferOptionIcon: /*#__PURE__*/_jsx(Icon, {
      iconKey: "supervisorAccountIcon"
    }),
    testId: 'attended-transfer',
    componentId: 'attended'
  });
};
var NewCallRow = function NewCallRow(_ref3) {
  var initCall = _ref3.initCall;
  var _useAppContext3 = useAppContext(),
    i18next = _useAppContext3.i18next;
  var primaryText = i18next.t('call.new');
  var secondaryText = i18next.t('call.new.info');
  return /*#__PURE__*/_jsx(TransferOptionRow, {
    initCall: initCall,
    primaryText: primaryText,
    secondaryText: secondaryText,
    transferOptionIcon: /*#__PURE__*/_jsx(Icon, {
      iconKey: "addCallIcon"
    }),
    testId: 'new-call',
    componentId: 'call'
  });
};
var CallTransferDialogContent = function CallTransferDialogContent(_ref4) {
  var transferNumber = _ref4.transferNumber,
    transferContact = _ref4.transferContact,
    handleNewCall = _ref4.handleNewCall,
    handleClose = _ref4.handleClose,
    handleTransferBlind = _ref4.handleTransferBlind,
    handleTransferAttended = _ref4.handleTransferAttended,
    thisExtensionIsUnavailable = _ref4.thisExtensionIsUnavailable;
  var _useAppContext4 = useAppContext(),
    i18next = _useAppContext4.i18next;
  var _useContact = useContact(),
    ownContact = _useContact.ownContact;
  var dispatch = useAppDispatch();
  var activeCall = useAppSelector(getFocusedActiveCall);
  var callerNumber = getNumberOfOtherPerson(activeCall);
  var getContact = useMemo(makeGetContactDataForNumber, []);
  var callerContact = useAppSelector(function (state) {
    return (
      //@ts-ignore
      getContact(state, callerNumber)
    );
  });
  var transferDescription = transferContact ? transferContact.get('description') : null;
  var prettyTransferNumber = prettyFormatE164Number(transferNumber, getCountryCode(ownContact, null));
  // A contact without a description has an empty string there, not null.
  var transferContactInfo = transferDescription || extractName(transferContact, prettyTransferNumber);
  var prettyCallerNumber = prettyFormatE164Number(callerNumber, getCountryCode(ownContact, null));
  var callerContactInfo = extractName(callerContact, prettyCallerNumber);
  useEffect(function () {
    if (callerNumber && !callerContact) {
      addUserPhoneLogEntry("Doing numberlookup on ".concat(callerNumber));
      dispatch(lookupNumber(callerNumber));
    }
  }, [callerNumber, callerContact]);
  return /*#__PURE__*/_jsx(Dialog, {
    open: true,
    onClose: handleClose,
    "data-test-id": 'call-transfer-dialog',
    className: "absolute inset-0 z-50 flex bg-black/50",
    as: "div",
    children: /*#__PURE__*/_jsxs(DialogPanel, {
      className: "mx-auto w-full max-w-md transform self-center overflow-hidden rounded-sm bg-white/100 p-4 dark:bg-gray-100",
      children: [/*#__PURE__*/_jsx(TransferTitleItem, {
        transferContactInfo: transferContactInfo,
        callerContactInfo: callerContactInfo
      }), thisExtensionIsUnavailable && /*#__PURE__*/_jsx(InfoBox, {
        type: InfoBoxType.Warning,
        children: i18next.t('General.Call.Transfer.Alert.Extension.Unavailable', {
          transferInfo: transferContactInfo
        })
      }), /*#__PURE__*/_jsx(BlindTransferRow, {
        initCall: handleTransferBlind
      }), /*#__PURE__*/_jsx(AttendedTransferRow, {
        initCall: handleTransferAttended
      }), handleNewCall && /*#__PURE__*/_jsx(NewCallRow, {
        initCall: handleNewCall
      }), /*#__PURE__*/_jsx("div", {
        className: "mt-5 flex w-full justify-end",
        children: /*#__PURE__*/_jsx(BaseButton, {
          onClick: handleClose,
          className: "hover:darken-20 text-gray-600 filter",
          children: i18next.t('Cancel')
        })
      })]
    })
  });
};

// A list row can open this dialog. Context passes through portals, so the
// dialog would otherwise take the row's Tab stop state.
var CallTransferDialog = function CallTransferDialog(props) {
  return /*#__PURE__*/_jsx(RowTabStopContext.Provider, {
    value: undefined,
    children: /*#__PURE__*/_jsx(CallTransferDialogContent, _objectSpread({}, props))
  });
};
export default /*#__PURE__*/React.memo(CallTransferDialog);